'use client'

import { useLayoutEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
// eslint-disable-next-line no-restricted-imports -- grandfathered; prefer next/image for new code
import { Anchor, Image, Stack, Text, Title } from '@mantine/core'
import { MedicationOption, ProductDetailPage } from '@/types/interfaces/productDetailPage'
import { buildSkuVideoDataConfig } from '@/lib/transformSkuPageContent'
import { SKU_BOOKING_REF_QUERY } from '@/lib/routes/skuBooking'
import { pageContentForMedicationOption } from '@/lib/productDetailPage/pageContentForMedicationOption'
import { MedicationOptionSelector } from './MedicationOptionSelector'
import { MedicationPriceCard } from './MedicationPriceCard'
import { RequestPrescriptionCta } from './RequestPrescriptionCta'
import { BrowsePharmaciesLink } from './BrowsePharmaciesLink'
import { ComparePharmaciesDrawer } from './ComparePharmaciesDrawer'
import { PDP_CLINICIAN_WIDGET_CONFIG, PDP_SUPPORT_WIDGET_CONFIG } from './sharedSectionsData'
import { SkuClinicianWidget } from '@/components/Refresh/SkuPages/SkuClinicianWidget/SkuClinicianWidget'
import { SkuSupportWidget } from '@/components/Refresh/SkuPages/SupportWidget/SkuSupportWidget'
import { WhatIsSkuWidget } from '@/components/Refresh/SkuPages/WhatIsSkuWidget/WhatIsSkuWidget'
import { SkuRelatedProductsByCategory } from '@/components/SkuRelatedProducts'
import { GreenSection } from '@/components/Refresh/CategoryPages/CategoryHeader/GreenSection'
import WidgetGenAi from '@/components/Refresh/CategoryPages/WidgetGenAi'
import { SkuPageTrustpilotReviews } from '@/components/shared/trustpilot'
import { FrequentlyAskedQuestions } from '@/components/FrequentlyAskedQuestions'
import classes from './ProductDetailPageContent.module.css'
import { appPath } from '@/lib/getPublicAppUrl'

interface ProductDetailPageContentProps {
    data: ProductDetailPage
}

export function ProductDetailPageContent({ data }: ProductDetailPageContentProps) {
    const [selectedOption, setSelectedOption] = useState<MedicationOption | undefined>(
        () => data.options?.find((option) => option.sku_id === data.selected_sku_id) ?? data.options?.[0],
    )
    const [isPharmacyDrawerOpen, setIsPharmacyDrawerOpen] = useState(false)
    const imageBoxRef = useRef<HTMLDivElement>(null)
    const contentColumnRef = useRef<HTMLDivElement>(null)
    // Margin that visually centers .contentColumn against .imageBox in the side-by-side
    // layout, computed from the column's collapsed height (see the effect below) and
    // then held fixed — so later height changes (the dose-form selector expanding) push
    // content below it downward instead of re-centering the whole column and shifting
    // the title. Null until the first measurement locks in — see the CSS module for how
    // .heroRow centers itself via plain CSS until then.
    const [contentTopOffset, setContentTopOffset] = useState<number | null>(null)
    const searchParams = useSearchParams()
    // Read via the hook (SSR-safe, unlike window.location.search) so the related-product
    // link below preserves booking-attribution params (e.g. ref) instead of dropping them.
    const searchParamsString = searchParams.toString()
    const bookingRef = searchParams.get(SKU_BOOKING_REF_QUERY)
    // Null when the selected dose form has no configured "what is" content — the
    // section is omitted rather than showing empty or placeholder copy.
    const selectedPageContent = selectedOption ? pageContentForMedicationOption(data, selectedOption) : null
    const whatIsConfig = selectedPageContent ? buildSkuVideoDataConfig(selectedPageContent) : null

    const handleOptionChange = (option: MedicationOption) => {
        setSelectedOption(option)
        // Rewrites the URL so a shared link deep-links to the dose form the customer chose,
        // rather than the PDP's default. The slug is cosmetic and doesn't change. Every
        // option, including its CMS page content, already lives in `data.options` from the
        // initial load, so no re-fetch is needed here — using the raw History API (instead
        // of next/navigation's router) updates the address bar without re-running the
        // route's server component for the new sku-id segment, which would otherwise
        // re-render the whole page.
        // Reuses the existing history.state (rather than passing null) so Next's App
        // Router still recognizes this history entry on a later Back/Forward navigation.
        const scrollY = window.scrollY
        window.history.replaceState(
            window.history.state,
            '',
            appPath(`/dp/${data.slug}/${option.sku_id}${window.location.search}`),
        )
        // Next.js patches history.replaceState to track navigations for its own router
        // state, and resets scroll to the top as a side effect — even for calls made
        // outside next/navigation like this one. Restore the scroll position on the
        // next frame, after that reset has already run.
        requestAnimationFrame(() => window.scrollTo(0, scrollY))
    }

    useLayoutEffect(() => {
        // Captured lazily, on the first measurement taken in the side-by-side layout,
        // so the offset below reflects only the collapsed height — never recomputed as
        // a reactive dependency, so expanding the selector later can't shift it. Reset
        // to null on a switch back to the mobile column layout so a later switch back
        // to side-by-side re-captures a fresh collapsed height instead of reusing a
        // stale one from before a layout change.
        let frozenCollapsedHeight: number | null = null

        const updateOffset = () => {
            // Side-by-side hero layout only — the mobile column layout (see the
            // matching @media (max-width: 900px) breakpoint in the CSS module) stacks
            // the image above the content, where a centering offset between the two
            // isn't meaningful.
            const isSideBySide = window.matchMedia('(min-width: 901px)').matches
            if (!isSideBySide) {
                frozenCollapsedHeight = null
                setContentTopOffset(null)
                return
            }

            if (frozenCollapsedHeight === null) {
                frozenCollapsedHeight = contentColumnRef.current?.offsetHeight ?? 0
            }
            const imageHeight = imageBoxRef.current?.offsetHeight ?? 0
            setContentTopOffset(Math.max(0, (imageHeight - frozenCollapsedHeight) / 2))
        }

        updateOffset()
        // .imageBox's height tracks the viewport via its aspect-ratio and flexible
        // width, so it's the only thing this offset needs to stay reactive to.
        const observer = new ResizeObserver(updateOffset)
        if (imageBoxRef.current) observer.observe(imageBoxRef.current)
        // The breakpoint can flip without imageBox's own size changing (e.g. only the
        // content column's width-driven wrapping changes at 900px), so it needs its own
        // listener rather than relying on the ResizeObserver above to catch it.
        const mediaQuery = window.matchMedia('(min-width: 901px)')
        mediaQuery.addEventListener('change', updateOffset)
        return () => {
            observer.disconnect()
            mediaQuery.removeEventListener('change', updateOffset)
        }
    }, [])

    if (!selectedOption) return null

    return (
        <>
            <Stack gap={32} className={classes.page}>
                <div className={classes.heroCard}>
                    <div className={classes.heroRowWrapper}>
                        <div
                            className={classes.heroRow}
                            data-offset-locked={contentTopOffset !== null ? 'true' : undefined}
                        >
                            <div className={classes.imageBox} ref={imageBoxRef}>
                                {(selectedOption.image_url ?? data.image_url) && (
                                    <Image
                                        src={selectedOption.image_url ?? data.image_url}
                                        alt={data.display_name}
                                        className={classes.image}
                                    />
                                )}
                            </div>
                            <Stack
                                gap={16}
                                className={classes.contentColumn}
                                ref={contentColumnRef}
                                style={contentTopOffset !== null ? { marginTop: contentTopOffset } : undefined}
                            >
                                <Stack gap={16}>
                                    <Title order={1} className={classes.title}>
                                        {data.display_name}
                                    </Title>
                                    {data.related_product && (
                                        <Text
                                            fz={18}
                                            fw="var(--gm-weight-text, 450)"
                                            c="var(--gm-foreground-fg-tertiary)"
                                        >
                                            {data.related_product.kind === 'brand' ? 'Generic for' : 'Brand for'}{' '}
                                            <Anchor
                                                component={Link}
                                                href={`/dp/${data.related_product.slug}/${data.related_product.sku_id}${searchParamsString ? `?${searchParamsString}` : ''}`}
                                                fz={18}
                                                c="var(--gm-foreground-fg-highlight)"
                                                underline="never"
                                                inherit
                                            >
                                                {data.related_product.name}
                                            </Anchor>
                                        </Text>
                                    )}
                                </Stack>

                                <MedicationOptionSelector
                                    options={data.options ?? []}
                                    selectedSkuId={selectedOption.sku_id}
                                    onChange={handleOptionChange}
                                />

                                <MedicationPriceCard skuId={selectedOption.sku_id} />

                                <RequestPrescriptionCta
                                    skuId={selectedOption.sku_id}
                                    skuHyphenatedName={selectedOption.sku_hyphenated_name}
                                    productName={data.display_name}
                                />

                                <BrowsePharmaciesLink onClick={() => setIsPharmacyDrawerOpen(true)} />
                            </Stack>
                        </div>
                    </div>

                    <GreenSection />
                </div>

                {whatIsConfig && <WhatIsSkuWidget key={selectedOption.sku_id} config={whatIsConfig} />}

                {/*
                 * The sections below are generic, platform-wide marketing content — identical
                 * on every medication PDP — reused from the shared SKU-page component library
                 * rather than rebuilt here.
                 */}
                <SkuClinicianWidget config={PDP_CLINICIAN_WIDGET_CONFIG} />
                <WidgetGenAi categoryName="medications" />
                <SkuPageTrustpilotReviews variant="medications" />
                <SkuSupportWidget config={PDP_SUPPORT_WIDGET_CONFIG} />
                <SkuRelatedProductsByCategory
                    category="base"
                    skuId={String(selectedOption.sku_id)}
                    skuName={selectedOption.sku_hyphenated_name}
                />
                <FrequentlyAskedQuestions />
            </Stack>
            <ComparePharmaciesDrawer
                opened={isPharmacyDrawerOpen}
                onClose={() => setIsPharmacyDrawerOpen(false)}
                skuId={selectedOption.sku_id}
                skuHyphenatedName={selectedOption.sku_hyphenated_name}
                productName={data.display_name}
                bookingRef={bookingRef}
            />
        </>
    )
}
